import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface SearchField {
  key: string;
  label: string;
  transform?: (value: any) => string;
  searchable?: boolean;
}

export interface GlobalSearchConfig {
  placeholder?: string;
  debounceTime?: number;
  searchFields: SearchField[];
  customFilterPredicate?: (data: any, filter: string) => boolean;
}

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule
  ]
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  @Input() config!: GlobalSearchConfig;
  @Input() dataSource: any;
  @Input() searchValue: string = '';
  
  @Output() searchChange = new EventEmitter<string>();
  @Output() searchCleared = new EventEmitter<void>();
  @Output() filterApplied = new EventEmitter<any>();

  private searchSubject = new Subject<string>();
  private originalFilterPredicate: any;

  ngOnInit(): void {
    // Set up debounced search
    this.searchSubject
      .pipe(
        debounceTime(this.config?.debounceTime || 300),
        distinctUntilChanged()
      )
      .subscribe(searchTerm => {
        this.performSearch(searchTerm);
      });

    // Store original filter predicate if dataSource has one
    if (this.dataSource && this.dataSource.filterPredicate) {
      this.originalFilterPredicate = this.dataSource.filterPredicate;
    }

    // Set up custom filter predicate
    this.setupFilterPredicate();
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.searchValue = value;
    this.searchChange.emit(value);
    this.searchSubject.next(value);
  }

  clearSearch(searchInput: HTMLInputElement): void {
    searchInput.value = '';
    this.searchValue = '';
    this.searchChange.emit('');
    this.searchCleared.emit();
    this.searchSubject.next('');
  }

  private setupFilterPredicate(): void {
    if (!this.dataSource) return;

    this.dataSource.filterPredicate = (data: any, filter: string): boolean => {
      if (!filter) {
        return true;
      }

      // Use custom filter predicate if provided
      if (this.config?.customFilterPredicate) {
        return this.config.customFilterPredicate(data, filter);
      }

      // Use default search logic
      return this.defaultFilterPredicate(data, filter);
    };
  }

  private defaultFilterPredicate(data: any, filter: string): boolean {
    const searchTerm = filter.toLowerCase();
    
    if (!this.config?.searchFields) {
      // Fallback to searching all string properties
      return this.searchAllStringProperties(data, searchTerm);
    }

    // Search in configured fields
    return this.config.searchFields.some(field => {
      if (field.searchable === false) return false;
      
      const value = this.getFieldValue(data, field.key);
      const searchableValue = field.transform ? field.transform(value) : this.convertToString(value);
      
      return searchableValue.toLowerCase().includes(searchTerm);
    });
  }

  private searchAllStringProperties(data: any, searchTerm: string): boolean {
    return Object.values(data).some(value => {
      if (typeof value === 'string') {
        return value.toLowerCase().includes(searchTerm);
      }
      if (Array.isArray(value)) {
        return value.some(item => 
          typeof item === 'string' && item.toLowerCase().includes(searchTerm)
        );
      }
      return false;
    });
  }

  private getFieldValue(data: any, key: string): any {
    // Support nested properties with dot notation
    return key.split('.').reduce((obj, prop) => obj?.[prop], data);
  }

  private convertToString(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return value.toString();
    }
    if (Array.isArray(value)) {
      return value.join(' ');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  private performSearch(searchTerm: string): void {
    if (this.dataSource) {
      this.dataSource.filter = searchTerm.trim();
      this.filterApplied.emit(this.dataSource.filteredData);
    }
  }
}
